import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Download, Smartphone } from "lucide-react";

const STORAGE_KEY = "mc-install-prompt-state";
// Show only after the 2nd authenticated app visit, so we don't ambush
// brand-new users. localStorage is keyed per-device, so each device gets
// at most one prompt ever (until the user clears storage).
const MIN_VISITS_BEFORE_PROMPT = 2;

function isIos() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS legacy
    window.navigator.standalone === true
  );
}

function readState() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function writeState(patch) {
  const next = { ...readState(), ...patch };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
}

/**
 * One-time PWA install nudge. Mounts globally; bails out unless:
 *   - user is logged in
 *   - app isn't already installed (standalone mode)
 *   - user has visited at least MIN_VISITS_BEFORE_PROMPT times
 *   - they haven't already dismissed or installed
 *
 * Chrome/Android: uses native beforeinstallprompt → real install dialog.
 * iOS Safari: falls back to a sonner toast with step-by-step instructions
 *             (no native API exists there).
 */
export function InstallPrompt() {
  const { user, loading } = useAuth();
  const deferredPromptRef = useRef(null);
  const firedRef = useRef(false);

  // Capture beforeinstallprompt eagerly so we can re-use it later.
  useEffect(() => {
    const onBefore = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
    };
    const onInstalled = () => {
      writeState({ installed_at: new Date().toISOString() });
      toast.success("MechaniCar installed — find it on your home screen.");
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Bump visit counter ONCE per page load, once auth state is known.
  useEffect(() => {
    if (loading || !user) return;
    const state = readState();
    if (state.bumped_this_session) return;
    const visits = (state.visits || 0) + 1;
    writeState({ visits, bumped_this_session: true, last_visit: new Date().toISOString() });
  }, [loading, user]);

  // Decide whether to fire the prompt toast.
  useEffect(() => {
    if (loading || !user || firedRef.current) return;
    if (isStandalone()) return; // already installed

    const state = readState();
    if (state.dismissed_at || state.installed_at || state.shown_at) return;
    if ((state.visits || 0) < MIN_VISITS_BEFORE_PROMPT) return;

    // For Android/Chrome we need the deferred prompt to have arrived first.
    // Give the browser ~3s to dispatch the event before deciding.
    const timer = setTimeout(() => {
      if (firedRef.current) return;
      const native = deferredPromptRef.current;
      const ios = isIos();
      if (!native && !ios) return; // unsupported browser (Firefox desktop, etc.) — skip silently
      firedRef.current = true;
      writeState({ shown_at: new Date().toISOString() });

      toast(
        <div className="flex items-start gap-3" data-testid="install-prompt-toast">
          <div className="w-8 h-8 bg-[#FF3B30]/15 border border-[#FF3B30]/30 flex items-center justify-center shrink-0">
            <Smartphone className="w-4 h-4 text-[#FF3B30]" strokeWidth={2.2} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-white text-sm">Install MechaniCar</div>
            <div className="text-xs text-zinc-300 mt-0.5">
              {ios
                ? "Tap the Share icon below, then \u201CAdd to Home Screen\u201D for one-tap access."
                : "Add it to your home screen for instant access — no app store needed."}
            </div>
            {!ios && (
              <button
                type="button"
                onClick={async () => {
                  const prompt = deferredPromptRef.current;
                  if (!prompt) return;
                  deferredPromptRef.current = null;
                  try {
                    await prompt.prompt();
                    const choice = await prompt.userChoice;
                    if (choice?.outcome === "accepted") {
                      writeState({ installed_at: new Date().toISOString() });
                    } else {
                      writeState({ dismissed_at: new Date().toISOString() });
                    }
                  } catch {
                    writeState({ dismissed_at: new Date().toISOString() });
                  } finally {
                    toast.dismiss();
                  }
                }}
                className="mt-2 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#FF3B30] hover:underline"
                data-testid="install-prompt-cta"
              >
                <Download className="w-3 h-3" /> Install now
              </button>
            )}
          </div>
        </div>,
        {
          duration: 14_000,
          onDismiss: () => writeState({ dismissed_at: new Date().toISOString() }),
          onAutoClose: () => writeState({ dismissed_at: new Date().toISOString() }),
        }
      );
    }, 3000);

    return () => clearTimeout(timer);
  }, [loading, user]);

  return null;
}
